import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Hub } from 'aws-amplify/utils';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signOut,
} from 'aws-amplify/auth';
import { BehaviorSubject } from 'rxjs';
import { AppAuthUser } from '../models/auth-user';
import { LoaderService } from './loader.service';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser: AppAuthUser | null = null;
  currentUserSubject = new BehaviorSubject<AppAuthUser | null>(null);

  _isAuthenticated = false;

  currentRoles: string[] = [];
  currentRolesSubject = new BehaviorSubject<string[]>([]);

  constructor(
    private _router: Router,
    private _loaderService: LoaderService,
    private _alertService: AlertService
  ) {
    Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          this._loaderService.hide();
          await this.refreshUserFromSession();
          window.location.reload();
          break;
        case 'signedOut':
          this.updateUserAndRoles(null);
          this._loaderService.hide();
          break;
        case 'signInWithRedirect_failure':
          this._loaderService.hide();
          break;
        default:
          break;
      }
    });

    this.currentUserSubject.subscribe((user) => {
      this.currentUser = user;
    });

    this.currentRolesSubject.subscribe((roles) => {
      this.currentRoles = roles;
    });
  }

  async authenticateUser(): Promise<boolean> {
    try {
      const user = await this.buildAppAuthUser();
      if (user) {
        this.updateUserAndRoles(user);
        return true;
      }
    } catch (error) {
      console.error(error);
      if (error === 'The user is not authenticated') {
        return true;
      }
    }
    return false;
  }

  private async refreshUserFromSession(): Promise<void> {
    const user = await this.buildAppAuthUser();
    this.updateUserAndRoles(user);
  }

  private async buildAppAuthUser(): Promise<AppAuthUser | null> {
    try {
      const cognitoUser = await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh: true });
      if (!session.tokens?.accessToken) {
        return null;
      }
      const attrs = await fetchUserAttributes();
      const attributes: AppAuthUser['attributes'] = {};
      for (const [key, value] of Object.entries(attrs)) {
        if (value !== undefined) {
          attributes[key] = String(value);
        }
      }
      return {
        username: cognitoUser.username,
        attributes,
        signInUserSession: {
          accessToken: {
            payload: session.tokens.accessToken.payload as Record<string, unknown>,
          },
          idToken: {
            toString: () => session.tokens!.idToken?.toString() ?? '',
          },
        },
      };
    } catch {
      return null;
    }
  }

  currentUserChanges() {
    return this.currentUserSubject;
  }

  updateUserAndRoles(user: AppAuthUser | null): void {
    if (!user) {
      this.currentUserSubject.next(null);
      this.currentRolesSubject.next([]);
      this._isAuthenticated = false;
      return;
    }

    this.currentUserSubject.next(user);
    this.currentUser = user;
    this._isAuthenticated = true;

    const raw = user.signInUserSession.accessToken.payload['cognito:groups'];
    const userRoles: string[] = Array.isArray(raw) ? (raw as string[]) : [];

    this.currentRolesSubject.next(userRoles);
  }

  getRoles(): string[] {
    return this.currentRoles;
  }

  getAuthenticatedUser(): AppAuthUser | null {
    return this.currentUser;
  }

  isAuthenticatedAsync(): Promise<boolean> {
    return getCurrentUser()
      .then(() => true)
      .catch(() => false);
  }

  isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  currentRolesChanges() {
    return this.currentRolesSubject;
  }

  async getAccessToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() ?? '';
    } catch {
      return '';
    }
  }

  async getIdToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? '';
    } catch {
      return '';
    }
  }

  async generateRequestHeaders(): Promise<HttpHeaders> {
    const access_token = await this.getAccessToken();
    const id_token = await this.getIdToken();
    let headers = new HttpHeaders();
    headers = headers.append('accesstoken', access_token);
    headers = headers.append('idtoken', id_token);
    return headers;
  }

  async signOut(): Promise<void> {
    this._loaderService.show();
    await signOut({ global: true });
  }
}
